import React from 'react';
import styled from 'styled-components';
import { formatSaldo } from '../../utils/formatters';
import { MONEDA_MOVIMIENTO, bucketDeMoneda } from '../../constants/monedas';

const Overlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(17, 24, 39, 0.8);
    display: flex; justify-content: center; align-items: center;
    z-index: 2050;
    @media (max-width: 600px) {
        padding: calc(env(safe-area-inset-top) + 0.5rem) 0.5rem calc(env(safe-area-inset-bottom) + 0.5rem);
    }
`;
const ModalContent = styled.div`
    background-color: ${props => props.theme.colors.surface};
    padding: 2rem;
    border-radius: 8px;
    width: 92%;
    max-width: 860px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    @media (max-width: 600px) {
        width: 100%;
        max-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 1rem);
        padding: 1rem;
    }
`;
const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
`;
const TitleWrap = styled.div`
    h3 {
        font-family: ${props => props.theme.typography.titleFamily};
        font-size: 1.4rem;
        color: ${props => props.theme.colors.primary};
        margin: 0 0 0.25rem 0;
    }
    p {
        margin: 0;
        font-size: 0.85rem;
        color: ${props => props.theme.colors.textSecondary};
    }
`;
const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1;
    &:hover { color: ${props => props.theme.colors.primary}; }
    @media (max-width: 600px) {
        min-width: 44px;
        min-height: 44px;
        margin: -0.5rem -0.5rem 0 0;
    }
`;
const MovementsScroll = styled.div`
    overflow-y: auto;
    flex: 1;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius};
`;
const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: 90px 1.1fr 1.3fr 150px 100px;
    gap: 0.75rem;
    padding: 0.5rem 0.9rem;
    background-color: rgba(26, 40, 62, 0.05);
    border-bottom: 1px solid ${props => props.theme.colors.border};
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => props.theme.colors.secondary};
    span:nth-child(4) { text-align: right; }
    @media (max-width: 600px) {
        display: none;
    }
`;
const MovementRow = styled.div`
    display: grid;
    grid-template-columns: 90px 1.1fr 1.3fr 150px 100px;
    gap: 0.75rem;
    padding: 0.6rem 0.9rem;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    font-size: 0.85rem;
    align-items: center;
    &:last-child { border-bottom: none; }
    /* Celular: tipo y monto arriba; cliente, detalle y estado debajo. */
    @media (max-width: 600px) {
        grid-template-columns: auto minmax(0, 1fr);
        row-gap: 0.3rem;
        & > :nth-child(2) { order: 2; grid-column: 1 / -1; font-weight: 600; }
        & > :nth-child(3) { order: 3; grid-column: 1 / -1; }
        & > :nth-child(4) { order: 1; text-align: right; }
        & > :nth-child(5) { order: 4; grid-column: 1 / -1; }
    }
`;
const TipoBadge = styled.span`
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
    background-color: ${props => {
        if (props.$tipo === 'caucion') return 'rgba(16, 185, 129, 0.15)';
        if (props.$tipo === 'pase') return 'rgba(234, 179, 8, 0.18)';
        if (props.$tipo === 'operacion') return 'rgba(59, 130, 246, 0.15)';
        if (props.$tipo === 'instruccion') return 'rgba(139, 92, 246, 0.15)';
        if (props.$tipo === 'licitacion') return 'rgba(244, 63, 94, 0.12)';
        return props.theme.colors.border;
    }};
    color: ${props => {
        if (props.$tipo === 'caucion') return '#065F46';
        if (props.$tipo === 'pase') return '#854D0E';
        if (props.$tipo === 'operacion') return '#1E40AF';
        if (props.$tipo === 'instruccion') return '#5B21B6';
        if (props.$tipo === 'licitacion') return '#9F1239';
        return props.theme.colors.secondary;
    }};
`;
const ClienteCell = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
`;
const MontoCell = styled.span`
    text-align: right;
    font-weight: 600;
    color: ${props => props.$positive ? '#065F46' : '#991B1B'};
    font-family: monospace;
    white-space: nowrap;
`;
const EstadoCell = styled.span`
    font-size: 0.75rem;
    color: ${props => props.theme.colors.textSecondary};
    text-align: right;
`;
const TotalBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 0.9rem 0 0.9rem;
    font-size: 0.9rem;
    font-weight: 700;
    color: ${props => props.theme.colors.secondary};
`;
const EmptySection = styled.div`
    padding: 0.85rem 0.9rem;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textSecondary};
    font-style: italic;
`;

const MovimientosDetalleModal = ({ isOpen, onClose, titulo, moneda, items, emptyText }) => {
    if (!isOpen) return null;

    const fmt = (value) => formatSaldo(value, moneda);
    const monedaLabel = MONEDA_MOVIMIENTO[bucketDeMoneda(moneda)];
    const sorted = [...items].sort((a, b) => Math.abs(b.monto) - Math.abs(a.monto));
    const total = items.reduce((acc, item) => acc + item.monto, 0);

    return (
        <Overlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <TitleWrap>
                        <h3>{titulo}</h3>
                        <p>{monedaLabel} — {items.length} {items.length === 1 ? 'movimiento' : 'movimientos'}</p>
                    </TitleWrap>
                    <CloseButton onClick={onClose} aria-label="Cerrar">×</CloseButton>
                </ModalHeader>

                <MovementsScroll>
                    {sorted.length === 0 ? (
                        <EmptySection>{emptyText || 'Sin movimientos para mostrar.'}</EmptySection>
                    ) : (
                        <>
                            <HeaderRow>
                                <span>Tipo</span><span>Cliente</span><span>Detalle</span><span>Monto</span><span>Estado</span>
                            </HeaderRow>
                            {sorted.map((mov, idx) => (
                                <MovementRow key={`${mov.tipo}-${mov.id || idx}`}>
                                    <TipoBadge $tipo={mov.tipo}>{mov.tipo}</TipoBadge>
                                    <ClienteCell title={mov.cliente}>{mov.cliente || 'N/A'}</ClienteCell>
                                    <span>{mov.descripcion}</span>
                                    <MontoCell $positive={mov.monto >= 0}>
                                        {mov.monto >= 0 ? '+' : ''}{fmt(mov.monto)}
                                    </MontoCell>
                                    <EstadoCell>{mov.estado}</EstadoCell>
                                </MovementRow>
                            ))}
                        </>
                    )}
                </MovementsScroll>
                {sorted.length > 0 && (
                    <TotalBar>
                        <span>Impacto neto</span>
                        <span>{fmt(total)}</span>
                    </TotalBar>
                )}
            </ModalContent>
        </Overlay>
    );
};

export default MovimientosDetalleModal;
